import type { ServerErrorMap } from '@hooks/useServerErrors';

/**
 * UI error targets for the order create + edit forms — form field names that
 * render a FieldError. Both fan out to two mutations (contact update + order
 * create/update), so this maps both surfaces' backend keys:
 *  - contact update emits its own field-error keys (email, mobile_number, …)
 *  - order create/update surfaces Order's own attributes via
 *    RecordInvalid#errors.to_hash: order.rb validates/errors.add on
 *    :salesperson (salesperson_must_be_sales_team), :order_source
 *    (presence), and the contact_name/contact_email/organisation_name it
 *    copies from the linked contact/organisation (set_contact_details /
 *    set_organisation_name) — distinct keys from the Contact model's own
 *    first_name/last_name/email above, since they're a different model.
 *
 * Keys not listed here fall through to a toast (see handleMutationError in
 * index.tsx) rather than silently vanishing.
 */
export type OrderServerErrorTarget =
  | 'billing_address'
  | 'billing_postal_code'
  | 'contact_email'
  | 'contact_name'
  | 'contact_phone'
  | 'order_source_id'
  | 'organisation_id'
  | 'payment_terms_in_days'
  | 'salesperson_id';

export const ORDER_SERVER_ERROR_TARGETS: ServerErrorMap<OrderServerErrorTarget> = {
  address: ['billing_address'],
  postal_code: ['billing_postal_code'],
  email: ['contact_email'],
  contact: ['contact_email'],
  contact_id: ['contact_email'],
  contact_email: ['contact_email'],
  first_name: ['contact_name'],
  last_name: ['contact_name'],
  full_name: ['contact_name'],
  contact_name: ['contact_name'],
  mobile_number: ['contact_phone'],
  phone_number: ['contact_phone'],
  order_source: ['order_source_id'],
  order_source_id: ['order_source_id'],
  organisation: ['organisation_id'],
  organisation_id: ['organisation_id'],
  organisation_name: ['organisation_id'],
  // Raised by the organisation endpoint the payment-terms field saves through,
  // e.g. a value past Organisation::MAX_PAYMENT_TERMS_IN_DAYS.
  payment_terms_in_days: ['payment_terms_in_days'],
  salesperson: ['salesperson_id'],
  salesperson_id: ['salesperson_id'],
};

export const ORDER_BASE_ERROR_TARGETS = [] as const;
